import axios from 'axios';

// Get configuration from environment variables with fallbacks
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10); // 30 seconds default (increased from 10)
const IS_DEVELOPMENT = process.env.REACT_APP_ENV === 'development' || process.env.NODE_ENV === 'development';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log API configuration in development
if (IS_DEVELOPMENT) {
  console.log('API Configuration:', {
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,
  });
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
            refresh: refreshToken,
          });
          const { access } = response.data;
          localStorage.setItem('access_token', access);
          return api(original);
        }
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
}

export interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
  message?: string;
}

export interface RegisterResponse {
  access_token: string;
  refresh_token: string;
  user: User;
  message?: string;
}

export interface ChatSession {
  session_id: string;
  title: string;
  status: string;
  message_count: number;
  created_at: string;
  last_message_at: string;
  last_message_preview?: string;
}

export interface ChatMessage {
  session_id: string;
  session_created: boolean;
  user_input: string;
  ai_response: string;
  extracted_info: any;
  search_results: SearchResult[];
  session_context: any;
  processing_time_ms: number;
  message_type?: string;
  phase?: string;
  completion_percentage?: number;
  ready_for_diagnosis?: boolean;
  isOptimistic?: boolean; // For UI-only optimistic updates
  follow_up_questions?: Array<{
    question: string;
    category: string;
    options?: string[];
    multiple_selection?: boolean;
  }>;
  followup_analysis?: {
    case_status?: string;
    similarity_score?: number;
    diagnosed_condition?: string;
    recommended_priority?: string;
    matching_confidence?: string;
    matched_case?: any;
    urgency_assessment?: string;
    diagnostic_direction?: string;
  };
}

// Orchestrated chat response format
export interface OrchestrateChatResponse {
  session_id: string;
  session_created: boolean;
  message: string;
  message_type: string;
  phase: string;
  extracted_info: any;
  search_results?: SearchResult[];
  all_cases?: Array<{
    case_number: number;
    score: number;
    is_relevant: boolean;
    relevance_level: string;
    priority: string;
    pet: string;
    age: string;
    gender: string;
    symptoms: string[];
    diagnosis: string;
    case_number_in_db: string;
    description: string;
    relevancy_reasoning: string;
  }>;
  follow_up_questions?: Array<{
    question: string;
    category: string;
    options?: string[];
    multiple_selection?: boolean;
  }>;
  completion_percentage: number;
  ready_for_diagnosis: boolean;
  session_ended: boolean;
  processing_time_ms: number;
}

export interface SearchResult {
  id: string;
  case_number: string;
  species: string;
  symptoms: string[];
  diagnosis: string;
  treatment: string;
  outcome: string;
  full_case_text: string;
  score: number;
  metadata: any;
}

export interface SimilaritySearchRequest {
  query_text: string;
  top_k?: number;
  species_filter?: string;
  min_score?: number;
}

export interface SimilaritySearchResponse {
  query_text: string;
  total_results: number;
  results: SearchResult[];
  search_time_ms: number;
}

export interface UploadedDocument {
  id: string;
  original_filename: string;
  status: string;
  upload_date: string;
  total_cases_extracted: number;
  error_message?: string;
}

export interface DocumentProcessingResult {
  document: UploadedDocument;
  total_cases: number;
  processed_cases: any[];
  message: string;
}

export interface IndexStats {
  total_vectors: number;
  dimension: number;
  index_fullness: number;
  namespace_stats: any;
}

export interface ContextualAnalysisRequest {
  user_input: string;
  session_id?: string;
  top_k?: number;
  include_conversation_context?: boolean;
}

export interface ContextualAnalysisResponse {
  session_id?: string;
  extraction_result: any;
  search_result: SimilaritySearchResponse;
  followup_result: any;
  conversation_context: any;
  session_summary: any;
  processing_time_ms: number;
}

// Chat Review interfaces
export interface ChatReview {
  id: string;
  user_username: string;
  session: string;
  session_title: string;
  review_type: 'general' | 'problem' | 'suggestion' | 'knowledgebase';
  content: string;
  created_at: string;
  updated_at: string;
  is_private: boolean;
  is_collaborative: boolean;
}

export interface ChatReviewCreate {
  session: string;
  review_type: 'general' | 'problem' | 'suggestion' | 'knowledgebase';
  content: string;
  is_private?: boolean;
}

export interface ChatReviewUpdate {
  review_type?: 'general' | 'problem' | 'suggestion' | 'knowledgebase';
  content?: string;
  is_private?: boolean;
}

// Review Management interfaces (Admin)
export interface ReviewSession {
  session_id: string;
  title: string;
  review_status: 'issue' | 'pending' | 'resolved';
  review_count: number;
  user_username: string;
  created_at: string;
  last_message_at: string;
  last_activity: string;
}

export interface ReviewSessionDetail {
  session_id: string;
  title: string;
  review_status: 'issue' | 'pending' | 'resolved';
  user_username: string;
  created_at: string;
  last_message_at: string;
  reviews: ChatReview[];
  message_count: number;
}

export interface ReviewStatusUpdate {
  review_status: 'issue' | 'pending' | 'resolved';
}

// Auth API
export const authAPI = {
  login: async (username: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/login/', { username, password });
    return response.data;
  },

  register: async (userData: {
    username: string;
    email: string;
    password: string;
    password_confirm: string;
    first_name: string;
    last_name: string;
  }): Promise<RegisterResponse> => {
    const response = await api.post('/auth/register/', userData);
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await api.get('/auth/profile/');
    return response.data;
  },

  updateProfile: async (userData: Partial<User>): Promise<User> => {
    const response = await api.put('/auth/profile/update/', userData);
    return response.data;
  },
};

// Chat API
export const chatAPI = {
  sendMessage: async (userInput: string, sessionId?: string): Promise<OrchestrateChatResponse> => {
    const response = await api.post('/chat/orchestrated/', {
      user_message: userInput,
      session_id: sessionId,
    });
    return response.data;
  },

  getBaseQuestions: async (): Promise<{ questions: any[]; greeting: string }> => {
    const response = await api.get('/chat/base-questions/');
    return response.data;
  },

  submitBaseQuestions: async (answers: Record<string, any>, sessionId?: string): Promise<OrchestrateChatResponse> => {
    const response = await api.post('/chat/base-questions/submit/', {
      answers,
      session_id: sessionId,
    });
    return response.data;
  },

  createSession: async (initialMessage?: string): Promise<ChatSession> => {
    const response = await api.post('/chat/sessions/', {
      initial_message: initialMessage || '',
    });
    return response.data;
  },

  getSessions: async (): Promise<{ sessions: ChatSession[]; total_sessions: number }> => {
    const response = await api.get('/chat/my-sessions/');
    return response.data;
  },

  getSessionHistory: async (sessionId: string): Promise<any> => {
    const response = await api.get(`/chat/sessions/${sessionId}/history/`);
    return response.data;
  },

  getSessionSummary: async (sessionId: string): Promise<any> => {
    const response = await api.get(`/chat/sessions/${sessionId}/summary/`);
    return response.data;
  },

  deleteSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/chat/sessions/${sessionId}/`);
  },

  shareSession: async (sessionId: string): Promise<{ share_url: string; is_shared: boolean; shared_at: string }> => {
    const response = await api.post(`/chat/sessions/${sessionId}/share/`);
    return response.data;
  },

  unshareSession: async (sessionId: string): Promise<{ message: string; is_shared: boolean }> => {
    const response = await api.post(`/chat/sessions/${sessionId}/unshare/`);
    return response.data;
  },

  getSharedSession: async (shareToken: string): Promise<any> => {
    // Shared endpoints need to go to the backend domain (not current origin)
    // Properly construct the backend base URL for shared endpoints
    const url = new URL(API_BASE_URL);
    const backendBaseUrl = `${url.protocol}//${url.host}`;
    const response = await axios.get(`${backendBaseUrl}/shared/chat/${shareToken}/`);
    return response.data;
  },

  getSharedSessionHistory: async (shareToken: string): Promise<any> => {
    // Shared endpoints need to go to the backend domain (not current origin)
    // Properly construct the backend base URL for shared endpoints
    const url = new URL(API_BASE_URL);
    const backendBaseUrl = `${url.protocol}//${url.host}`;
    const response = await axios.get(`${backendBaseUrl}/shared/chat/${shareToken}/history/`);
    return response.data;
  },

  // Review API methods
  createReview: async (reviewData: ChatReviewCreate): Promise<ChatReview> => {
    const response = await api.post('/chat/reviews/', reviewData);
    return response.data;
  },

  getSessionReviews: async (sessionId: string): Promise<{ session_id: string; session_title: string; total_reviews: number; reviews: ChatReview[] }> => {
    const response = await api.get(`/chat/sessions/${sessionId}/reviews/`);
    return response.data;
  },

  getUserReviews: async (): Promise<{ total_reviews: number; reviews: ChatReview[] }> => {
    const response = await api.get('/chat/reviews/my/');
    return response.data;
  },

  getUserSessionsWithNotes: async (note_type?: string, page?: number, page_size?: number): Promise<{
    count: number;
    next: number | null;
    previous: number | null;
    results: Array<{
      session_id: string;
      title: string;
      created_at: string;
      updated_at: string;
      last_message_at: string;
      message_count: number;
      note_count: number;
      note_types: string[];
      is_shared: boolean;
    }>;
    note_type_counts: {
      general: number;
      problem: number;
      suggestion: number;
      knowledgebase: number;
    };
  }> => {
    const params = new URLSearchParams();
    if (note_type) params.append('note_type', note_type);
    if (page) params.append('page', page.toString());
    if (page_size) params.append('page_size', page_size.toString());
    
    const response = await api.get(`/chat/sessions/my/with-notes/?${params.toString()}`);
    return response.data;
  },

  updateReview: async (reviewId: string, reviewData: ChatReviewUpdate): Promise<ChatReview> => {
    const response = await api.put(`/chat/reviews/${reviewId}/`, reviewData);
    return response.data;
  },

  deleteReview: async (reviewId: string): Promise<void> => {
    await api.delete(`/chat/reviews/${reviewId}/`);
  },

  // Admin Review Management
  getReviewSessions: async (status?: string, page?: number, page_size?: number, note_type?: string): Promise<{ 
    count: number; 
    next: string | null; 
    previous: string | null; 
    results: ReviewSession[] 
  }> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (note_type) params.append('note_type', note_type);
    if (page) params.append('page', page.toString());
    if (page_size) params.append('page_size', page_size.toString());
    
    const response = await api.get(`/admin/review/sessions/?${params.toString()}`);
    return response.data;
  },

  getReviewSessionDetail: async (sessionId: string): Promise<ReviewSessionDetail> => {
    const response = await api.get(`/admin/review/sessions/${sessionId}/`);
    return response.data;
  },

  updateReviewStatus: async (sessionId: string, status: 'issue' | 'pending' | 'resolved'): Promise<{ review_status: string; message: string }> => {
    const response = await api.patch(`/admin/review/sessions/${sessionId}/status/`, { review_status: status });
    return response.data;
  },

  bulkUpdateReviewStatus: async (sessionIds: string[], status: 'issue' | 'pending' | 'resolved'): Promise<{ 
    updated_count: number; 
    failed_count: number; 
    message: string; 
    errors: string[] 
  }> => {
    const response = await api.post('/admin/review/sessions/bulk-update-status/', { 
      session_ids: sessionIds, 
      review_status: status 
    });
    return response.data;
  },

  adminShareSession: async (sessionId: string): Promise<{ share_url: string; is_shared: boolean; shared_at: string }> => {
    const response = await api.post(`/admin/review/sessions/${sessionId}/share/`);
    return response.data;
  },
};

// Search API
export const searchAPI = {
  similaritySearch: async (request: SimilaritySearchRequest): Promise<SimilaritySearchResponse> => {
    const response = await api.post('/search/', request);
    return response.data;
  },

  contextualAnalysis: async (request: ContextualAnalysisRequest): Promise<ContextualAnalysisResponse> => {
    const response = await api.post('/chat/analyze/', request);
    return response.data;
  },

  getIndexStats: async (): Promise<IndexStats> => {
    const response = await api.get('/stats/');
    return response.data;
  },
};

// Document API
export const documentAPI = {
  uploadDocument: async (file: File): Promise<DocumentProcessingResult> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/documents/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getDocuments: async (): Promise<UploadedDocument[]> => {
    const response = await api.get('/documents/');
    return response.data.results || response.data;
  },

  getDocument: async (documentId: string): Promise<UploadedDocument> => {
    const response = await api.get(`/documents/${documentId}/`);
    return response.data;
  },
};

// Health check
export const healthAPI = {
  healthCheck: async (): Promise<any> => {
    const response = await api.get('/health/');
    return response.data;
  },
};

export default api; 